import { Parser } from 'expr-eval-fork';
import LocaleService from 'services/loader-handler-services/locale-service';

// StateParser
//
// The expression language shared by StateService and TriggerService.
//
// This consolidates the two parsers that had drifted apart - `TwineParser`
// (strings, arrays, dates, formatNumber, translate) and the calculation block's
// `createCalculationParser` (object/array helpers plus a course's finance
// model). Everything here is general purpose; course-specific functions are
// registered at runtime through `registerFunctions` instead of being compiled
// into the SDK.
//
// Two things about expr-eval-fork shape how StateService uses this:
//
// * `$name` is a valid identifier. Twine string-replaced `$vars` into the
//   expression text before parsing, which is where its quoting hacks and its
//   "undefined variable: null" crashes came from. We pass a scope object with
//   `$`-prefixed keys instead and never rewrite the expression.
// * `parse(expr).variables()` reports the identifiers an expression reads. That
//   is what lets StateService derive a dependency graph and report missing
//   variables by name without the author declaring anything.
//
// Divergences from TwineParser, deliberate, for the twine transformer to know
// about: `slice` now returns the slice, not the untouched array, and
// `substr`/`substring` are spelled correctly (TwineParser called `.subStr()`
// and `.subString()`, which always threw). `push`/`sort` keep twine's
// semantics; the calculation block's object-building and keyed variants are
// available as `pushEntries`/`sortBy` so neither dialect loses a capability.

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// Functions a course registered for itself, applied to every parser we create
// so that a parser built after the course booted still gets them.
const registeredFunctions = {};

const addStringFunctions = function (parser) {
  parser.functions.strlen = function (str) {
    if (!str && parseInt(str) !== 0) {
      return 0;
    }
    return String(str).length;
  };
  parser.functions.concat = function (...args) {
    return args[0].concat(...args.slice(1));
  };
  parser.functions.includes = function (...args) {
    return args[0].includes(...args.slice(1));
  };
  parser.functions.match = function (...args) {
    return args[0].match(...args.slice(1));
  };
  parser.functions.replace = function (...args) {
    return args[0].replace(...args.slice(1));
  };
  parser.functions.split = function (...args) {
    return args[0].split(...args.slice(1));
  };
  parser.functions.substr = function (...args) {
    return String(args[0]).substr(...args.slice(1));
  };
  parser.functions.substring = function (...args) {
    return String(args[0]).substring(...args.slice(1));
  };
  parser.functions.toUpperCase = function (str) {
    return String(str).toUpperCase();
  };
  parser.functions.toLowerCase = function (str) {
    return String(str).toLowerCase();
  };
  parser.functions.toStr = function (...args) {
    return args[0].toString(...args.slice(1));
  };
  // Retained under twine's name so transformed expressions keep working.
  parser.functions.toString2 = parser.functions.toStr;
  parser.functions.trim = function (str) {
    return String(str).trim();
  };
};

const addArrayFunctions = function (parser) {
  // Mutating helpers return the array so they can be chained in a compute
  // expression, which is how twine content uses them.
  parser.functions.push = function (...args) {
    args[0].push(...args.slice(1));
    return args[0];
  };
  parser.functions.pop = function (arr) {
    arr.pop();
    return arr;
  };
  parser.functions.shift = function (arr) {
    arr.shift();
    return arr;
  };
  parser.functions.unshift = function (...args) {
    args[0].unshift(...args.slice(1));
    return args[0];
  };
  parser.functions.splice = function (...args) {
    args[0].splice(...args.slice(1));
    return args[0];
  };
  parser.functions.reverse = function (arr) {
    return arr.reverse();
  };
  parser.functions.sort = function (arr) {
    return arr.sort();
  };
  parser.functions.slice = function (...args) {
    return args[0].slice(...args.slice(1));
  };
  parser.functions.lastIndexOf = function (...args) {
    return args[0].lastIndexOf(...args.slice(1));
  };

  // Build an object from ['key', value] pairs and append it. The calculation
  // block spelled this `push`; it is a different operation from appending an
  // item, so it gets its own name here.
  parser.functions.pushEntries = function (...args) {
    args[0].push(Object.fromEntries(args.slice(1)));
    return args[0];
  };

  // Descending sort by a key of each member object.
  parser.functions.sortBy = function (arr, key) {
    return arr.sort((a, b) => {
      if (a[key] > b[key]) {
        return -1;
      }
      if (a[key] < b[key]) {
        return 1;
      }
      return 0;
    });
  };

  parser.functions.fromEntries = function (entries) {
    return Object.fromEntries(entries);
  };
  parser.functions.toEntries = function (value) {
    return Object.entries(value);
  };
  parser.functions.jsonParse = function (jsonStr) {
    return JSON.parse(jsonStr);
  };
};

const addDateFunctions = function (parser) {
  parser.functions.date = function (...args) {
    return new Date(...args);
  };
  parser.functions.getDate = function (...args) {
    return new Date(...args).getDate();
  };
  parser.functions.getDay = function (...args) {
    return new Date(...args).getDay();
  };
  parser.functions.getDayStr = function (...args) {
    return DAY_NAMES[new Date(...args).getDay()];
  };
  parser.functions.getMonth = function (...args) {
    return new Date(...args).getMonth();
  };
  parser.functions.getMonthStr = function (...args) {
    return MONTH_NAMES[new Date(...args).getMonth()];
  };
  parser.functions.getFullYear = function (...args) {
    return new Date(...args).getFullYear();
  };
  parser.functions.getHours = function (...args) {
    return new Date(...args).getHours();
  };
  parser.functions.getMinutes = function (...args) {
    return new Date(...args).getMinutes();
  };
  parser.functions.getSeconds = function (...args) {
    return new Date(...args).getSeconds();
  };
  parser.functions.getMilliseconds = function (...args) {
    return new Date(...args).getMilliseconds();
  };
  parser.functions.getTime = function (...args) {
    return new Date(...args).getTime();
  };
  parser.functions.toDateString = function (...args) {
    return new Date(...args).toDateString();
  };
  parser.functions.toTimeString = function (...args) {
    return new Date(...args).toTimeString();
  };
  parser.functions.toLocaleTimeString = function (...args) {
    return new Date(...args).toLocaleTimeString();
  };
};

const addCourseFunctions = function (parser) {
  // `translate(key)` looks a key up in the course's locale; `translate(key, n)`
  // passes a count, so i18next picks the singular or plural entry. The count
  // form matters more than it looks: twine content builds sentences like
  // "3 years 2 months" by concatenating hard-coded English words, which is why
  // whole blocks end up duplicated per language.
  parser.functions.translate = function (name, count) {
    return count === undefined ? LocaleService.t(name) : LocaleService.t(name, { count: count });
  };

  parser.functions.regex = function (regexp, value, operation) {
    if (!regexp || !value) {
      return false;
    }
    if (operation === 'match') {
      const matchArr = value.match(regexp);
      return matchArr && matchArr.length ? matchArr[0] : '';
    }
    return new RegExp(regexp).test(value);
  };

  // Kept for expressions carried over from twine. New content should store the
  // number and put `format: currency` on whatever displays it - see
  // StateFormatService, so formatting stays out of the expression.
  parser.functions.formatNumber = function (num, lang, digits, code) {
    return parseFloat(num).toLocaleString(lang, {
      minimumFractionDigits: digits,
      style: 'currency',
      currency: code,
    });
  };
};

// Build a parser with the full function set. Callers that need an isolated
// parser (tests, a block that registers throwaway functions) can make their
// own; everything else shares the default instance below.
const createStateParser = function () {
  const parser = new Parser();

  addStringFunctions(parser);
  addArrayFunctions(parser);
  addDateFunctions(parser);
  addCourseFunctions(parser);

  Object.entries(registeredFunctions).forEach(([name, fn]) => {
    parser.functions[name] = fn;
  });

  return parser;
};

const StateParser = createStateParser();

// Let a course add its own functions - the finance model that is currently
// compiled into the calculation block's parser belongs here instead. Call this
// from the course's startup before any page renders.
const registerFunctions = function (functions) {
  Object.entries(functions).forEach(([name, fn]) => {
    if (typeof fn !== 'function') {
      console.error(`StateParser: '${name}' is not a function and was not registered`);
      return;
    }
    registeredFunctions[name] = fn;
    StateParser.functions[name] = fn;
  });
};

// The identifiers an expression reads. Used for dependency ordering and for
// telling an author which variable is missing, so a parse failure has to be
// reported.
const getExpressionVariables = function (expression) {
  return StateParser.parse(expression).variables({ withMembers: false });
};

export default StateParser;
export { createStateParser, registerFunctions, getExpressionVariables };
